// Pure replay loop. Given an initial state + ordered actions, drives the
// engine through the same auto-advance / combat-resolution choreography
// the live game uses in `main.ts:processAction`, but without any UI or
// animation. Used by:
//
//   - `tools/replay-log.ts`         — re-runs recorded matches for Phase 1
//                                     regression checks.
//   - eventual server log validator (Phase 4).
//   - the AI tree search (Phase 3) when it needs to simulate a chain of
//     actions from a hypothetical state.
//
// The action set this consumes is exactly the user-action set recorded by
// `actionLog`: every move dispatched in the live flow, including the
// phase-transition pseudo-actions (SKIP_PHASE, CONFIRM_BLOCKERS).

import type { GameState, GameAction, CardDef, GameEvent } from '../types.js';
import { dispatch } from './GameState.js';
import { advancePhase, advanceUntilInteractive, skipToCounterPhase } from './TurnManager.js';
import { resolveCombat } from './CombatResolver.js';
import { getMaxHandSize } from './AbilityProcessor.js';

export interface ReplayStep {
  /** 0-based index into the input action array. */
  index: number;
  action: GameAction;
  events: GameEvent[];
  /** Phase the engine landed in after this action's auto-advance ran. */
  postPhase: GameState['phase'];
  /** Winner after this action, if any. */
  winner: GameState['winner'];
}

export interface ReplayResult {
  state: GameState;
  steps: ReplayStep[];
  /** True if the game ended before all recorded actions were consumed.
   *  When true, `remainingActions` is the tail that was skipped. */
  endedEarly: boolean;
  remainingActions: GameAction[];
}

/** Run a sequence of actions through the engine starting from `state`.
 *  Returns the final state plus per-step events. Stops early (returning
 *  `endedEarly: true`) if a winner is set partway through — the live game
 *  can't dispatch past game-over either, so the replay does the same. */
export function replayActions(
  initialState: GameState,
  actions: GameAction[],
  catalog: Map<string, CardDef>,
): ReplayResult {
  let state = initialState;
  const steps: ReplayStep[] = [];

  for (let i = 0; i < actions.length; i++) {
    if (state.winner) {
      return {
        state,
        steps,
        endedEarly: true,
        remainingActions: actions.slice(i),
      };
    }

    const action = actions[i];
    const events: GameEvent[] = [];

    if (action.type === 'SKIP_PHASE') {
      const a = advancePhase(state, catalog);
      state = a.state;
      events.push(...a.events);
      const b = advanceUntilInteractive(state, catalog);
      state = b.state;
      events.push(...b.events);
    } else if (action.type === 'CONFIRM_BLOCKERS') {
      const r = resolveCombat(state, catalog);
      state = r.state;
      events.push(...r.events);
      if (!state.winner) {
        const b = advanceUntilInteractive(state, catalog);
        state = b.state;
        events.push(...b.events);
      }
    } else if (action.type === 'DECLARE_ATTACKERS') {
      const r = dispatch(state, action, catalog);
      state = r.state;
      events.push(...r.events);
      if (!state.winner) {
        const c = skipToCounterPhase(state, catalog);
        state = c.state;
        events.push(...c.events);
      }
    } else if (action.type === 'DISCARD_CARD') {
      const r = dispatch(state, action, catalog);
      state = r.state;
      events.push(...r.events);
      // The live loop auto-advances out of cleanup once the hand is back
      // under the limit. Mirror it so subsequent actions land at the same
      // phase boundary as the recording.
      if (state.phase === 'cleanup') {
        const ps = state.currentPlayer === 'player' ? state.player : state.opponent;
        if (ps.hand.length <= getMaxHandSize(state, state.currentPlayer, catalog)) {
          const b = advanceUntilInteractive(state, catalog);
          state = b.state;
          events.push(...b.events);
        }
      }
    } else {
      const r = dispatch(state, action, catalog);
      state = r.state;
      events.push(...r.events);
    }

    steps.push({
      index: i,
      action,
      events,
      postPhase: state.phase,
      winner: state.winner,
    });
  }

  return { state, steps, endedEarly: false, remainingActions: [] };
}
